import React, { lazy } from "react";
import { Navigate, useRoutes } from "react-router-dom";
// layouts
import DashboardLayout from "./layouts/dashboard";
import SimpleLayout from "./layouts/simple";
import PrivateRoute from "./privateRoute";

// Lazy loading is used to optimize performance.
const DashboardPermissionPage = lazy(() =>
  import("././pages/dashboardPermission/DashboardPermission")
);
const TeacherPage = lazy(() => import("./pages/teacher/Teacher"));
const TaskDetailsPage = lazy(() => import("./pages/taskDetails/TaskDetails"));
const TaskDetailsValuePage = lazy(() => import("./pages/taskDetails/taskDetailValue"));

const EditTeacherPage = lazy(() => import("./pages/teacher/EditTeacher"));
const CreateTeacherPage = lazy(() => import("./pages/teacher/CreateTeacher"));
const SubscriptionsPage = lazy(() =>
  import("./pages/subscriptions/Subscriptions")
);
const BillingPage = lazy(() => import("./pages/billing/Billing"));
const SupportPage = lazy(() => import("./pages/support/support"));
const CredentialsPage = lazy(() => import("./pages/credentials/Credentials"));

const FAQs = lazy(() => import("./pages/FAQs/FAQs"));

const UploadTaskPage = lazy(() => import("./pages/uploadTask/UploadTask"));
const UploadTaskByValuePage = lazy(() => import("./pages/uploadTaskByValue/UploadTaskByValue"));

const ViewDetail = lazy(() => import("./pages/taskDetails/viewTaskDeatail"));
const ContactUsPage = lazy(() => import("./pages/contactUs/CreateContactUs"));
const Profile = lazy(() => import("./pages/Profile"));
const AccountPage = lazy(() => import("./pages/accountPage/account"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const Page404 = lazy(() => import("./pages/Page404"));
const SignUpPage = lazy(() => import("./pages/SignUp"));
const VerificationAccount =  lazy(() => import("./pages/VerificationAccount"));
// const ForgotEmail = lazy(() => import("./pages/ForgotEmail"));
// const ResetPassword = lazy(() => import("./pages/Reset-Passwod"));
const ForgotEmail = lazy(() => import("./pages/ForgotEmail"));
const ResetPassword = lazy(() => import("./pages/Reset-Passwod"));
const LandingPage = lazy(() => import('./pages/landing/Landing'));

const Policies = lazy(() => import("./pages/policies/Policies"));
const PrivacyPolicy = lazy(() => import("./pages/privacyPolicy/PrivacyPolicy"));
const Ses = lazy(() => import("./pages/ses/Ses"));

export default function Router() {
  /**
   * Create routes and Implement permissions on routes
   *
   * Author: Muhammad Rooman
   * Date: 20 August, 2024
   */

  const isAuthenticated = !!localStorage.getItem("clientToken");

  const routes = useRoutes([
    {path: '/',
      element: <LandingPage />,
      index: true
    },
    {
      path: "/",
      element: <DashboardLayout />,
      children: [
        { element: <Navigate to="/dashboard" />, index: true },
        {
          path: "dashboard",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<DashboardPermissionPage />}
            />
          ),
        },
        {
          path: "teachers",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<TeacherPage />}
            />
          ),
        },
        {
          path: "teachers/create",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<CreateTeacherPage />}
            />
          ),
        },
        {
          path: "teachers/edit/:id",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<EditTeacherPage />}
            />
          ),
        },
        {
          path: "subscriptions",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<SubscriptionsPage />}
            />
          ),
        },
        // {
        //   path: "support",
        //   element: (
        //     <PrivateRoute
        //       isAuthenticated={isAuthenticated}
        //       element={<SupportPage />}
        //     />
        //   ),
        // },
        {
          path: "billing",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<BillingPage />}
            />
          ),
        },
        {
          path: "upload-task",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<UploadTaskPage />}
            />
          ),
        },
        {
          path: "task-upload-by-value",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<UploadTaskByValuePage />}
            />
          ),
        },
        {
          path: "task-details/:id",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<TaskDetailsPage />}
            />
          ),
        },
        {
          path: "task-details-value",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<TaskDetailsValuePage />}
            />
          ),
        },
        {
          path: "view-details/:id",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<ViewDetail />}
            />
          ),
        },
        {
          path: "profile",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<Profile />}
            />
          ),
        },
        {
          path: "account",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<AccountPage />}
            />
          ),
        },
        {
          path: "contact-us",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<ContactUsPage />}
            />
          ),
        },
        // FAQ's
        {
          path: "faq",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<FAQs />}
            />
          ),
        },
        {
          path: "credentials",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<CredentialsPage />}
            />
          ),
        },
        {
          path: "policies",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<Policies />}
            />
          ),
        },
        
        {
          path: "ses",
          element: (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              element={<Ses />}
            />
          ),
        },
      ],
    },
    {
      path: "privacy-policy",
      element: <PrivacyPolicy />,
    },
    {
      path: "/login",
      element: <LoginPage />,
    },
    {
      path: "/sign-up",
      element: <SignUpPage />,
    },
    {
      path: "/forgot-email",
      element: <ForgotEmail />,
    },
    {
      path: "/verification-account/:token",
      element: <VerificationAccount />,
    },
    {
      path: "/reset-password/:id",
      element: <ResetPassword />,
    },
    {
      element: <SimpleLayout />,
      children: [
        { element: <Navigate to="/login" />, index: true },
        {
          path: "404",
          element: <Page404 />,
        },
        { path: "*", element: <Navigate to="/404" /> },
      ],
    },
    {
      path: "*",
      element: <Navigate to="/404" replace />,
    },
  ]);

  return routes;
}
